import { EditorState, Modifier } from "draft-js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { insertTimestampIntoText } from "../../helpers/getTimestamp";
import { getEmployees } from "../../services/getEmployees";
import ButtonAddTimestamp from "../../utils/ButtonAddTimestamp";

const NoteEditor = ({
    label,
    notes,
    saveNotes,
    setEditNotes,
    name,
    loggedInEmployee,
}) => {
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [mentionValue, setMentionValue] = useState(notes || "");
    const [mentionedEmployeesIds, setMentionedEmployeesIds] = useState([]);
    const [employees, setEmployees] = useState([]);
    const notesRef = useRef(null);
    const mentionPattern = /\[[^\]]+\]\(([^)]+)\)/g;

    const extractMentionedIds = (text) => {
        return [...(text || "").matchAll(mentionPattern)].map((match) => match[1]);
    };

    useEffect(() => {
        const getNewEmployees = async () => {
            const response = await getEmployees();
            console.log("Employees response:", response);
            if (response.data.success) {
                const formattedEmployees = response.data.employees.map((emp) => ({
                    id: emp.employeeId,
                    display: emp.firstName + " " + emp.lastName,
                }));
                setEmployees(formattedEmployees);
                setMentionedEmployeesIds(extractMentionedIds(notes || ""));
            }
        };
        getNewEmployees();
    }, []);

    const handleMentionChange = (e) => {
        const newValue = e.target.value;
        setMentionValue(newValue);

        const newMentions = extractMentionedIds(newValue);

        const removedMentions = mentionedEmployeesIds.filter(
            (id) => !newMentions.includes(id)
        );

        if (removedMentions.length > 0) {
            console.log("Removed mentions:", removedMentions);
            // Handle any follow-up work needed when mentions are removed.
        }

        setMentionedEmployeesIds(newMentions);
    };

    const clickOnSave = () => {
        console.log("Notes and mentions:", {
            notes: mentionValue,
            mentionedEmployeesIds: mentionedEmployeesIds,
        });
        setEditNotes(false);
        saveNotes({
            notes: mentionValue,
            mentionedEmployeesIds: mentionedEmployeesIds,
        });
    };

    const addTimestamp = () => {
        const textarea = notesRef.current;
        if (!textarea) return;

        const current = mentionValue || "";

        if (document.activeElement !== textarea) {
            textarea.focus();
            textarea.setSelectionRange(current.length, current.length);
        }

        const start =
            typeof textarea.selectionStart === "number"
                ? textarea.selectionStart
                : current.length;
        const end =
            typeof textarea.selectionEnd === "number"
                ? textarea.selectionEnd
                : start;

        const { caretPosition, value: updated } = insertTimestampIntoText(
            current,
            start,
            end,
            loggedInEmployee,
        );

        setMentionValue(updated);
        setMentionedEmployeesIds(extractMentionedIds(updated));

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                textarea.focus();
                textarea.setSelectionRange(caretPosition, caretPosition);
            });
        });
    };

    const handleAddMention = useCallback(
        (id) => {
            const user = employees.find((user) => user.id === id);

            if (user) {
                const contentState = editorState.getCurrentContent();
                const selection = editorState.getSelection();

                const contentStateWithMention = Modifier.insertText(
                    contentState,
                    selection,
                    `@${user.display} `
                );

                const newEditorState = EditorState.push(
                    editorState,
                    contentStateWithMention,
                    "insert-characters"
                );

                setEditorState(newEditorState);

                if (!mentionedEmployeesIds.includes(user.id)) {
                    setMentionedEmployeesIds([...mentionedEmployeesIds, user.id]);
                    console.log("Mention added:", mentionValue);
                }
            }
        },
        [editorState, employees]
    );

    return (
        <div className="editor-container">
            <div className="overflow-auto mentions-container">
                <label className="block text-sm font-medium text-gray-500">
                    {label}
                </label>
                <MentionsInput
                    inputRef={notesRef}
                    name={name}
                    value={mentionValue}
                    onChange={handleMentionChange}
                    placeholder="Mentions start with @"
                    className="mentions-input height-100 "
                    style={{
                        resize: "both",
                        overflow: "auto",
                        height: "100px",
                        border: "1px solid gray",
                    }}
                >
                    <Mention
                        trigger="@"
                        data={employees}
                        displayTransform={(id) => {
                            const user = employees.find((user) => user.id === id);
                            return user ? `@${user.display}` : `@${id}`;
                        }}
                        appendSpaceOnAdd={true}
                        onAdd={handleAddMention}
                        style={{
                            backgroundColor: "#E0F2FE",
                            overflow: "auto",
                            height: "100px",
                        }}
                        renderSuggestion={(
                            entry,
                            search,
                            highlightedDisplay,
                            index
                        ) => (
                            <div
                                key={index}
                                style={{
                                    overflow: "auto",
                                    padding: "8px",
                                    cursor: "pointer",
                                    backgroundColor: "#fff",
                                    transition: "background-color 0.2s ease-in-out",
                                }}
                                onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        "#e0e0e0")
                                }
                                onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#fff")
                                }
                            >
                                {highlightedDisplay}
                            </div>
                        )}
                    />
                </MentionsInput>
                <div className="mt-2">
                    <ButtonAddTimestamp onHandleClick={addTimestamp} />
                </div>
            </div>

            <div className="flex justify-end w-full my-4">
                <button
                    onClick={() => setEditNotes(false)}
                    className="h-10 mr-4 text-white bg-red-500 btn hover:bg-red-600"
                >
                    Cancel
                </button>
                <button
                    onClick={() => clickOnSave()}
                    className="h-10 text-white bg-teal-500 btn hover:bg-teal-600"
                >
                    Save
                </button>
            </div>
            {/*  <div className="draft-editor-container">
                <Editor
                    editorState={editorState}
                    onChange={handleEditorChange}
                    placeholder="Type in your notes here..."
                    customStyleMap={styleMap}
                />
            </div> */}
        </div>
    );
};

export default NoteEditor;
